import { floorToEuro, formatEUR, formatSignedEUR } from '@budget/shared';
import { Link } from 'react-router-dom';
import { useAccounts, useDashboard } from '../api/hooks';
import { RemainingCurve } from '../components/RemainingCurve';
import { SavingsSummary } from '../components/SavingsSummary';
import { SavingsWithdrawModal } from '../components/SavingsWithdrawModal';
import { useRef, useState } from 'react';

/**
 * L'écart porte une couleur de statut. Celles-ci ne se distinguent pas assez
 * sous daltonisme deutan pour se suffire à elles-mêmes : la flèche et le mot
 * « avance » ou « retard » sont donc obligatoires, pas décoratifs.
 */
function DriftBadge({ driftCents }: { driftCents: number }) {
  const ahead = driftCents >= 0;
  return (
    <p className={`mt-1 text-sm font-medium ${ahead ? 'text-success-text' : 'text-critical'}`}>
      <span aria-hidden="true">{ahead ? '▲' : '▼'}</span>{' '}
      {formatSignedEUR(driftCents)} {ahead ? 'd’avance' : 'de retard'}
      <span className="text-ink-muted"> sur la trajectoire</span>
    </p>
  );
}

export function DashboardScreen() {
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  // Déclarés avant les retours anticipés ci-dessous : les hooks doivent
  // s'exécuter dans le même ordre à chaque rendu.
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  // Le haut de cette carte fixe jusqu'où la feuille de renflouement doit
  // couvrir l'écran — voir SavingsWithdrawModal.
  const curveSectionRef = useRef<HTMLDivElement>(null);
  const accounts = useAccounts();
  const dashboard = useDashboard(accountId);

  if (dashboard.isPending) {
    return <p className="px-6 py-10 text-ink-muted">Chargement…</p>;
  }

  if (dashboard.isError) {
    return (
      <div className="px-6 py-10">
        <p className="text-ink-secondary">
          Aucun compte courant pour l’instant. Créez-en un pour voir votre reste à vivre.
        </p>
        <Link
          to="/comptes"
          className="mt-4 inline-block rounded-xl bg-series px-4 py-3 text-white"
        >
          Créer un compte
        </Link>
      </div>
    );
  }

  const { snapshot, curve, account } = dashboard.data;
  const checking = (accounts.data ?? []).filter((item) => item.type === 'checking');
  const dailyCents = floorToEuro(snapshot.dailyAllowanceCents);
  // Sous 30 € par jour, la couleur alerte ; à zéro ou moins, la tuile bascule
  // en porte d'entrée vers le renflouement (branche distincte plus bas).
  const dailyLow = dailyCents < 3_000;

  /*
   * Sens du mouvement proposé sur les pôles d'épargne.
   *
   * Un solde négatif l'emporte sur l'avance : on peut être en avance sur la
   * trajectoire tout en étant à découvert, si un gros revenu reste à percevoir.
   * Dans ce cas, c'est le découvert qui commande — proposer d'épargner
   * davantage serait absurde.
   */
  const needsCash = account.balanceCents < 0 || snapshot.driftCents < 0;
  const moveDirection = needsCash ? 'withdraw' : 'deposit';
  const suggestedMoveCents = needsCash
    ? Math.max(-account.balanceCents, -snapshot.driftCents, 0)
    : Math.max(snapshot.driftCents, 0);

  return (
    <div className="flex flex-col gap-6 px-5 pt-4">
      <header className="flex items-baseline gap-2">
        <h1 className="text-2xl font-bold tracking-tight">Budget</h1>
        <p className="text-sm text-ink-muted">- gestion du compte courant</p>
      </header>

      {/* Le nom du compte n'a plus de ligne dédiée quand il n'y en a qu'un :
          le sous-titre de l'en-tête le dit déjà. Le sélecteur reste, lui, une
          vraie fonction — choisir entre plusieurs comptes — pas une simple
          mention redondante. */}
      {checking.length > 1 && (
        <select
          value={account.id}
          onChange={(event) => setAccountId(event.target.value)}
          className="self-start rounded-lg border border-line bg-surface px-3 py-1.5 text-sm"
          aria-label="Compte affiché"
        >
          {checking.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      )}

      <section className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Un seul héros par écran. Chiffres proportionnels, pas tabulaires.
              Tronqué à l'euro inférieur : mieux vaut promettre un peu moins. */}
          <p className="text-5xl font-semibold tracking-tight">
            {formatEUR(floorToEuro(snapshot.remainingCents), { round: true })}
          </p>
          <DriftBadge driftCents={snapshot.driftCents} />
        </div>

        <div className="w-32 shrink-0 rounded-2xl border border-line bg-surface px-3 py-3 text-center">
          {dailyCents <= 0 ? (
            // Solde à sec : la tuile devient une porte d'entrée vers le
            // renflouement plutôt qu'un chiffre qu'il n'y a plus rien à lire.
            <button
              type="button"
              onClick={() => setWithdrawOpen(true)}
              className="text-[11px] leading-tight font-medium text-critical underline decoration-dotted underline-offset-2"
            >
              <span aria-hidden="true">⚠ </span>
              Alerte, solde négatif - alimenter le compte.
            </button>
          ) : (
            <>
              <p className="text-[11px] leading-tight text-ink-muted">Dépenses max / jour</p>
              <p className="mt-1 text-sm">
                <span className={`font-semibold ${dailyLow ? 'text-critical' : ''}`}>
                  {formatEUR(dailyCents, { round: true })}
                </span>
              </p>
              <p className="mt-0.5 text-[11px] text-ink-muted">
                sur {snapshot.cycle.remainingDays} jour
                {snapshot.cycle.remainingDays > 1 ? 's' : ''}
              </p>
            </>
          )}
        </div>
      </section>

      {withdrawOpen && (
        <SavingsWithdrawModal
          accountId={account.id}
          anchorRef={curveSectionRef}
          onClose={() => setWithdrawOpen(false)}
        />
      )}

      <section ref={curveSectionRef} className="rounded-2xl border border-line bg-surface p-3">
        <RemainingCurve points={curve} remainingCents={snapshot.remainingCents} />
      </section>

      {/* L'état du compte décide du sens : on met de côté quand il reste du
          gras, on reprend quand il manque d'air. */}
      <SavingsSummary
        accountId={account.id}
        accountBalanceCents={account.balanceCents}
        accountIcon={checking.find((item) => item.id === account.id)?.icon ?? ''}
        direction={moveDirection}
        suggestedCents={suggestedMoveCents}
        anchorRef={curveSectionRef}
      />

      <details className="rounded-2xl border border-line bg-surface p-4">
        <summary className="cursor-pointer text-sm text-ink-secondary">
          Comment ces chiffres sont calculés
        </summary>

        {/* Le reste à vivre part du solde réel : il est donc juste même si des
            dépenses n'ont pas été saisies, ou si l'app a été adoptée en cours
            de mois. */}
        <p className="mt-3 text-xs font-medium text-ink-muted uppercase">Reste à vivre</p>
        <dl className="mt-1.5 grid grid-cols-[1fr_auto] gap-y-1.5 text-sm">
          <dt className="text-ink-secondary">Solde du compte</dt>
          <dd className="tabular">{formatEUR(snapshot.balanceCents)}</dd>
          <dt className="text-ink-secondary">Revenus encore à percevoir</dt>
          <dd className="tabular">+{formatEUR(snapshot.upcomingIncomeCents)}</dd>
          <dt className="text-ink-secondary">Charges encore à prélever</dt>
          <dd className="tabular">−{formatEUR(snapshot.upcomingExpenseCents)}</dd>
          <dt className="text-ink-secondary">Épargne encore à virer</dt>
          <dd className="tabular">−{formatEUR(snapshot.upcomingSavingsCents)}</dd>
          <dt className="border-t border-line pt-1.5 font-medium">Reste à vivre</dt>
          <dd className="tabular border-t border-line pt-1.5 font-medium">
            {formatEUR(snapshot.remainingCents)}
          </dd>
        </dl>

        <p className="mt-4 text-xs font-medium text-ink-muted uppercase">Trajectoire</p>
        <dl className="mt-1.5 grid grid-cols-[1fr_auto] gap-y-1.5 text-sm">
          <dt className="text-ink-secondary">Revenus fixes du mois</dt>
          <dd className="tabular">{formatEUR(snapshot.plannedIncomeCents)}</dd>
          <dt className="text-ink-secondary">Charges fixes du mois</dt>
          <dd className="tabular">−{formatEUR(snapshot.plannedExpenseCents)}</dd>
          <dt className="text-ink-secondary">Épargne programmée</dt>
          <dd className="tabular">−{formatEUR(snapshot.plannedSavingsCents)}</dd>
          <dt className="border-t border-line pt-1.5 font-medium">Budget du mois</dt>
          <dd className="tabular border-t border-line pt-1.5 font-medium">
            {formatEUR(snapshot.budgetVariableCents)}
          </dd>
          <dt className="text-ink-secondary">
            Attendu au soir du jour {snapshot.cycle.elapsedDays}
          </dt>
          <dd className="tabular">{formatEUR(snapshot.idealRemainingCents)}</dd>
        </dl>

        <p className="mt-3 text-xs text-ink-muted">
          Dépenses saisies sur le cycle : {formatEUR(snapshot.spentCents)}. Elles font baisser le
          solde, donc le reste à vivre — mais le chiffre reste juste même si l’une d’elles a été
          oubliée.
        </p>
      </details>
    </div>
  );
}
